import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  concat,
  debounceTime,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import { SupplierService, SupplierApiActions } from '@ps/survey-data-access';
import {
  isAllocatableSupplier,
  Quota,
  SupplierAllocation,
  Survey,
  SupplierAllocationsEditableContext,
  SurveyMetadataForm,
  SurveyQualification,
} from '@ps/survey-utils';

import { SurveyForm } from '../../utils';
import { FeasibilityService } from '../../services/feasibility/feasibility.service';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
  SurveyCreationNavbarActions,
  SurveyCreationNavbarApiActions,
} from './survey-creation-audience.actions';
import {
  selectSurvey,
  selectSurveyFormState,
  selectSurveyId,
} from './survey-creation-audience.selectors';
import {
  qualifiesForRecalculation,
  shouldMarkAsPending,
} from './utils/survey-creation-recalculation-trigger.helper';
import { FlexibilityPricesService } from '../../services/flexibility-prices/flexibility-prices.service';

interface RecalculationData {
  readonly user: { id: number; email: string; companyId: number };
  readonly surveyId: number;
  readonly survey: Option<Survey>;
  readonly metadata: SurveyMetadataForm;
  readonly qualifications: SurveyQualification[];
  readonly quotas: Quota[];
}

// Suppliers → flexibility prices → feasibility, strictly in that order, on every
// qualifying survey change. A new edit cancels whatever was still in flight via
// the switchMap chain, so only the latest debounced edit ever lands in state.
@Injectable()
export class SurveyCreationRecalculatePricingEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _supplierService = inject(SupplierService);
  private readonly _flexibilityPricesService = inject(FlexibilityPricesService);
  private readonly _feasibilityService = inject(FeasibilityService);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _authSelectors = inject(AuthSelectorsFacade);

  public readonly recalculateFeasibility$ = createEffect(() => {
    const surveyChangeTrigger$ = this._actions$.pipe(
      ofType(
        // SurveyCreationApiActions.createStandardSurveySuccess,
        SurveyCreationApiActions.loadSurveyForEditSuccess,
        SurveyCreationApiActions.saveSurveySuccess,
        SurveyCreationActions.updateMetadata,
        SurveyCreationActions.updateQualifications,
        SurveyCreationActions.updateQuotas,
        SurveyCreationNavbarActions.toggleManualProcessing,
        SurveyCreationNavbarActions.recalculateFeasibility,
      ),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
    );

    return merge(
      surveyChangeTrigger$.pipe(
        filter(([action]) => shouldMarkAsPending(action)),
        map(() =>
          SurveyCreationNavbarApiActions.recalculateFeasibilityPending(),
        ),
      ),
      surveyChangeTrigger$.pipe(
        filter(([action]) =>
          qualifiesForRecalculation(
            action,
            !!this._userSettingsSelectors.buyerSettings()
              .enableManualProcessing,
          ),
        ),
        debounceTime(500),
        map(([, surveyId]) => Option.from(surveyId).unwrap()),
        concatLatestFrom(() => [
          this._store.select(selectSurveyFormState),
          this._store.select(selectSurvey),
        ]),
        map(([surveyId, formState, survey]) => ({
          surveyId,
          survey: Option.from(survey),
          form: SurveyForm.from(formState),
        })),
        filter(({ form }) => form.valid),
        map(({ surveyId, survey, form }) => ({
          user: this._userData(),
          surveyId,
          survey,
          ...Option.from(form.data).unwrap(),
        })),
        switchMap((data) => this._recalculateFlexibilityPricing$(data)),
      ),
    );
  });

  // Reacts to the update PUT landing. Its response already carries the updated
  // allocations (and the reducer has already stored them), so this re-runs only
  // flexibility → feasibility, reading the allocations straight off the action
  // rather than refetching them or reaching into the slice.
  public readonly refreshPricingAfterSupplierUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectSurvey),
      ]),
      filter(([, surveyId]) => !!surveyId),
      map(([{ allocations }, surveyId, formState, survey]) => ({
        allocations,
        surveyId: Option.from(surveyId).unwrap(),
        survey: Option.from(survey),
        form: SurveyForm.from(formState),
      })),
      filter(({ form }) => form.valid),
      map(({ allocations, surveyId, survey, form }) => ({
        allocations,
        data: {
          user: this._userData(),
          surveyId,
          survey,
          ...Option.from(form.data).unwrap(),
        },
      })),
      switchMap(({ data, allocations }) =>
        concat(
          of(SurveyCreationNavbarApiActions.recalculateFeasibilityPending()),
          this._loadFlexibilityPrices$(data, allocations),
        ),
      ),
    ),
  );

  private _recalculateFlexibilityPricing$(
    data: RecalculationData,
  ): Observable<Action> {
    return this._supplierService
      .allocations(data.surveyId, {
        scope: 'editable',
        context: this._toSupplierContext(data),
      })
      .pipe(
        switchMap((allocations) =>
          concat(
            of(SupplierApiActions.loadAllocationsSuccess({ allocations })),
            this._loadFlexibilityPrices$(data, allocations),
          ),
        ),
        this.psCatchError(
          (error) =>
            of(
              SupplierApiActions.loadAllocationsFailure({
                error: error.message ?? 'Failed to load supplier allocations',
              }),
            ),
          { notify: true },
        ),
      );
  }

  private _loadFlexibilityPrices$(
    data: RecalculationData,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    return this._flexibilityPricesService.get(data.surveyId).pipe(
      switchMap((prices) =>
        concat(
          of(
            SurveyCreationNavbarApiActions.loadFlexibilityPricesSuccess({
              prices,
            }),
          ),
          this._computeFeasibility$(data, allocations),
        ),
      ),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationNavbarApiActions.loadFlexibilityPricesFailure({
              error: error.message ?? 'Failed to load flexibility prices',
            }),
          ),
        { notify: true },
      ),
    );
  }

  private _computeFeasibility$(
    data: RecalculationData,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    return this._feasibilityService
      .compute({
        surveyId: data.surveyId,
        user: data.user,
        metadata: data.metadata,
        qualifications: data.qualifications,
        quotas: data.quotas,
        supplierIds: this._activeSupplierIds(allocations),
      })
      .pipe(
        map((feasibility) =>
          SurveyCreationNavbarApiActions.recalculateFeasibilitySuccess(
            feasibility,
          ),
        ),
        this.psCatchError((error) =>
          of(
            SurveyCreationNavbarApiActions.recalculateFeasibilityFailure({
              error: error.message,
            }),
          ),
        ),
      );
  }

  // Groups expand to their individual member ids; a Set absorbs the
  // duplicate standalone rows the backend also sends per group member.
  // Shares `isAllocatableSupplier` with the CPI drawer's row mapper so the
  // supplier set sent here always matches the one the drawer shows as checked.
  private _activeSupplierIds(
    allocations: readonly SupplierAllocation[],
  ): number[] {
    const ids = new Set<number>();

    allocations.filter(isAllocatableSupplier).forEach((allocation) => {
      if (allocation.isGroup) {
        allocation.suppliers.forEach((member) => ids.add(member.id));
      } else {
        ids.add(allocation.id);
      }
    });

    return [...ids];
  }

  private _toSupplierContext(
    data: RecalculationData,
  ): SupplierAllocationsEditableContext {
    return {
      loi: data.metadata.loi ?? 0,
      ir: data.metadata.incidence ?? 0,
      languageCode: data.survey
        .andThen((survey) => Option.from(survey.languageCode))
        .unwrapOr(''),
      fieldTime: data.metadata.fieldTime ?? 0,
      goal: data.metadata.completes ?? 0,
      clickBalancing: data.metadata.clickBalancing,
    };
  }

  /**
   * Unwrapped, not pipelined: every action these effects listen for originates
   * on the create-survey screen, which is behind the auth guard, so a session is
   * a structural invariant here. `activeCompanyId()` unwraps the same Option
   * internally, so guarding only this call would move the throw, not remove it.
   */
  private _userData(): { id: number; email: string; companyId: number } {
    const user = this._authSelectors.user().unwrap();
    return {
      id: user.id,
      email: user.email,
      companyId: this._authSelectors.activeCompanyId(),
    };
  }
}
